<template>
  <div>
    <!-- ── Controls bar ──────────────────────────────────────────────────── -->
    <div class="d-flex align-items-center justify-content-between mb-3">
      <h6 class="mb-0 section-title">Event Analytics</h6>
      <div class="d-flex align-items-center" style="gap:8px;">
        <label class="pill-input mb-0">
          <select v-model="days" @change="load" style="border:none;background:transparent;font-size:13px;font-weight:600;color:#0f172a;appearance:none;">
            <option value="7">Last 7 days</option>
            <option value="14">Last 14 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
        </label>
        <button class="btn btn-dark" @click="load" :disabled="loading">
          <i class="bi bi-arrow-clockwise" :class="{ 'anim-spin': loading }"></i>
        </button>
      </div>
    </div>

    <!-- ── Search ────────────────────────────────────────────────────────── -->
    <div class="card flat-card mb-4">
      <div class="card-body p-3">
        <div class="d-flex align-items-center mb-2" style="gap:8px;">
          <div class="pill-input flex-grow-1">
            <i class="bi bi-search text-secondary"></i>
            <input
              v-model="search.query"
              @input="onSearchInput"
              @keydown.esc="clearSearch"
              type="text"
              placeholder="Search routes, event names… (e.g. /api/products, checkout_started)"
              autocomplete="off"
            />
            <i v-if="search.query" class="bi bi-x-lg text-secondary" style="cursor:pointer;" @click="clearSearch"></i>
          </div>

          <!-- Type filter -->
          <div class="btn-group" role="group">
            <button
              v-for="opt in searchTypes"
              :key="opt.value"
              class="btn"
              :class="search.type === opt.value ? 'btn-dark' : 'btn-outline-secondary'"
              @click="search.type = opt.value; runSearch()"
            >{{ opt.label }}</button>
          </div>
        </div>

        <div class="text-secondary" style="font-size:.72rem;">
          Searches across the last <strong>{{ days }} days</strong> of activity.
          Use <strong>Feature</strong> mode to search by feature name (e.g. "Inventory", "POS", "Payroll").
        </div>
      </div>
    </div>

    <!-- ── Search results ────────────────────────────────────────────────── -->
    <template v-if="search.query.length >= 2">
      <div v-if="search.loading" class="text-center py-4">
        <div class="spinner-border spinner-border-sm text-secondary"></div>
        <p class="text-secondary mt-2 mb-0">Searching…</p>
      </div>

      <template v-else-if="search.results">
        <!-- Feature breadcrumb when drilling into a feature -->
        <div v-if="search.results.feature" class="d-flex align-items-center mb-3">
          <span class="badge badge-dark px-3 py-2" style="font-size:.82rem;">
            <i class="bi bi-grid mr-1"></i>
            Feature: {{ search.results.feature }}
          </span>
          <span class="text-secondary ml-2">
            {{ (search.results.routes.length + search.results.events.length) }} items · last {{ days }} days
          </span>
          <button class="btn btn-link text-secondary ml-auto" @click="clearSearch">
            <i class="bi bi-x"></i> Clear
          </button>
        </div>

        <!-- No results -->
        <div
          v-if="!search.results.routes.length && !search.results.events.length"
          class="alert alert-light text-center"
        >
          <i class="bi bi-binoculars mr-1"></i>
          No results for <strong>"{{ search.query }}"</strong> in the last {{ days }} days.
        </div>

        <template v-else>
          <!-- Route results -->
          <div v-if="search.results.routes.length" class="card flat-card mb-4">
            <div class="card-body p-0">
              <div class="d-flex align-items-center justify-content-between px-3 pt-3 pb-2">
                <h6 class="font-weight-bold mb-0">
                  <i class="bi bi-signpost-2 mr-1 text-dark"></i>
                  Routes
                  <span class="badge badge-dark badge-pill ml-1" style="font-size:.7rem;">{{ search.results.routes.length }}</span>
                </h6>
                <small class="text-secondary">matching "{{ search.query }}"</small>
              </div>
              <div style="overflow-x:auto;">
                <table class="table table-hover mb-0">
                  <thead class="thead-light">
                    <tr>
                      <th>Route / Path</th>
                      <th class="text-right" style="width:90px;">Visits</th>
                      <th class="text-right" style="width:90px;">Users</th>
                      <th class="text-right" style="width:90px;">Companies</th>
                      <th class="text-right" style="width:130px;">Last Seen</th>
                      <th style="width:160px;"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in search.results.routes" :key="row.route">
                      <td>
                        <code class="text-break" style="word-break:break-all;">
                          <span v-html="highlight(row.path, search.query)"></span>
                        </code>
                      </td>
                      <td class="text-right">
                        <span class="font-weight-bold">{{ fmt(row.visits) }}</span>
                      </td>
                      <td class="text-right text-secondary">{{ fmt(row.unique_users) }}</td>
                      <td class="text-right text-secondary">{{ fmt(row.companies) }}</td>
                      <td class="text-right text-secondary">{{ timeAgo(row.last_seen) }}</td>
                      <td>
                        <div class="progress mt-1" style="height:5px;border-radius:3px;">
                          <div
                            class="progress-bar bg-dark"
                            :style="{ width: barWidth(row.visits, search.results.routes[0].visits) + '%' }"
                          ></div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Event results -->
          <div v-if="search.results.events.length" class="card flat-card mb-4">
            <div class="card-body p-0">
              <div class="d-flex align-items-center justify-content-between px-3 pt-3 pb-2">
                <h6 class="font-weight-bold mb-0">
                  <i class="bi bi-lightning-charge mr-1 text-dark"></i>
                  Events
                  <span class="badge badge-dark badge-pill ml-1" style="font-size:.7rem;">{{ search.results.events.length }}</span>
                </h6>
                <small class="text-secondary">matching "{{ search.query }}"</small>
              </div>
              <div style="overflow-x:auto;">
                <table class="table table-hover mb-0">
                  <thead class="thead-light">
                    <tr>
                      <th>Event Name</th>
                      <th>Type</th>
                      <th>Source</th>
                      <th class="text-right" style="width:90px;">Count</th>
                      <th class="text-right" style="width:90px;">Users</th>
                      <th class="text-right" style="width:90px;">Companies</th>
                      <th class="text-right" style="width:130px;">Last Seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in search.results.events" :key="row.event_name + row.source">
                      <td>
                        <code >
                          <span v-html="highlight(row.event_name, search.query)"></span>
                        </code>
                      </td>
                      <td>
                        <span class="badge badge-light text-capitalize border">{{ row.event_type || '—' }}</span>
                      </td>
                      <td>
                        <span
                          class="badge"
                          :class="{
                            'badge-primary': row.source === 'frontend',
                            'badge-secondary': row.source === 'middleware',
                            'badge-dark': row.source === 'backend',
                          }"
                        >{{ row.source }}</span>
                      </td>
                      <td class="text-right">
                        <span class="font-weight-bold">{{ fmt(row.occurrences) }}</span>
                      </td>
                      <td class="text-right text-secondary">{{ fmt(row.unique_users) }}</td>
                      <td class="text-right text-secondary">{{ fmt(row.companies) }}</td>
                      <td class="text-right text-secondary">{{ timeAgo(row.last_seen) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </template>
      </template>

      <div v-else-if="search.error" class="alert alert-danger">
        Search failed: {{ search.error }}
      </div>
    </template>

    <!-- ── Main dashboard (shown when not searching) ─────────────────────── -->
    <template v-if="!search.query">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border spinner-border-sm text-secondary"></div>
        <p class="text-muted mt-2 mb-0">Loading analytics…</p>
      </div>

      <template v-else-if="data">
        <!-- KPI cards -->
        <div class="row mb-4">
          <div class="col-6 col-md-3 mb-3">
            <div class="card flat-card h-100">
              <div class="card-body p-3">
                <div class="text-muted mb-1">Total Events</div>
                <div class="h4 mb-0 font-weight-bold">{{ fmt(data.totalEvents) }}</div>
                <div class="text-muted mt-1">last {{ days }} days</div>
              </div>
            </div>
          </div>
          <div class="col-6 col-md-3 mb-3">
            <div class="card flat-card h-100">
              <div class="card-body p-3">
                <div class="text-muted mb-1">Unique Users</div>
                <div class="h4 mb-0 font-weight-bold">{{ fmt(data.uniqueUsers) }}</div>
                <div class="text-muted mt-1">active</div>
              </div>
            </div>
          </div>
          <div class="col-6 col-md-3 mb-3">
            <div class="card flat-card h-100">
              <div class="card-body p-3">
                <div class="text-muted mb-1">Frontend Events</div>
                <div class="h4 mb-0 font-weight-bold">{{ fmt(data.frontendEvents) }}</div>
                <div class="text-muted mt-1">UI-tracked</div>
              </div>
            </div>
          </div>
          <div class="col-6 col-md-3 mb-3">
            <div class="card flat-card h-100">
              <div class="card-body p-3">
                <div class="text-muted mb-1">Events / User</div>
                <div class="h4 mb-0 font-weight-bold">
                  {{ data.uniqueUsers > 0 ? (data.totalEvents / data.uniqueUsers).toFixed(1) : '—' }}
                </div>
                <div class="text-muted mt-1">avg engagement</div>
              </div>
            </div>
          </div>
        </div>

        <!-- ── Feature Breakdown ─────────────────────────────────────────── -->
        <div class="card flat-card mb-4">
          <div class="card-body p-3">
            <div class="d-flex align-items-center justify-content-between mb-3">
              <h6 class="font-weight-bold mb-0">
                <i class="bi bi-grid mr-1"></i> Feature Usage
              </h6>
              <small class="text-muted">last {{ days }} days — click to drill down</small>
            </div>

            <div v-if="featuresBreakdownLoading" class="text-center py-3">
              <div class="spinner-border spinner-border-sm text-secondary"></div>
            </div>

            <div v-else-if="featuresBreakdown.length" class="row">
              <div
                v-for="feat in featuresBreakdown"
                :key="feat.feature"
                class="col-6 col-md-4 col-lg-3 mb-3"
              >
                <div
                  class="card border-0 h-100 feature-card"
                  :style="{ borderLeft: '3px solid var(--feat-color, #6c757d) !important', cursor: 'pointer' }"
                  :class="'border-left border-' + feat.color"
                  @click="drillSearch(feat.feature, 'feature')"
                  :title="'Drill into ' + feat.feature"
                >
                  <div class="card-body p-2">
                    <div class="d-flex align-items-start justify-content-between">
                      <div class="flex-grow-1 min-width-0">
                        <div class="font-weight-bold text-truncate">{{ feat.feature }}</div>
                        <div class="h5 mb-0 font-weight-bold mt-1">{{ fmt(feat.visits) }}</div>
                        <div class="text-muted" style="font-size:.68rem;">
                          {{ feat.unique_users }} users · {{ feat.companies }} co.
                        </div>
                      </div>
                      <i :class="['bi', feat.icon, 'ml-2 text-' + feat.color]" style="font-size:1.4rem;opacity:.7;flex-shrink:0;"></i>
                    </div>
                    <div class="progress mt-2" style="height:3px;border-radius:2px;">
                      <div
                        class="progress-bar"
                        :class="'bg-' + feat.color"
                        :style="{ width: barWidth(feat.visits, featuresBreakdown[0].visits) + '%' }"
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <p v-else class="text-muted text-center py-3 mb-0">No feature data yet.</p>
          </div>
        </div>

        <div class="row">
          <!-- Activity timeline -->
          <div class="col-12 mb-4">
            <div class="card flat-card">
              <div class="card-body p-3">
                <h6 class="font-weight-bold mb-3">Activity Timeline</h6>
                <div v-if="timeline.length" style="overflow-x:auto;">
                  <table class="table table-borderless mb-0" style="min-width:600px;">
                    <thead>
                      <tr class="text-muted">
                        <th style="width:90px;">Date</th>
                        <th>Activity</th>
                        <th class="text-right" style="width:80px;">Frontend</th>
                        <th class="text-right" style="width:80px;">Middleware</th>
                        <th class="text-right" style="width:80px;">Backend</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in timeline" :key="row.date">
                        <td class="text-muted">{{ row.date }}</td>
                        <td>
                          <div class="progress" style="height:8px;border-radius:4px;">
                            <div
                              class="progress-bar bg-primary"
                              :style="{ width: barWidth(row.total, maxTimelineTotal) + '%' }"
                            ></div>
                          </div>
                        </td>
                        <td class="text-right">{{ fmt(row.frontend) }}</td>
                        <td class="text-right">{{ fmt(row.middleware) }}</td>
                        <td class="text-right">{{ fmt(row.backend) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p v-else class="text-muted text-center py-3 mb-0">No timeline data yet.</p>
              </div>
            </div>
          </div>

          <!-- Event type breakdown -->
          <div class="col-md-5 mb-4">
            <div class="card flat-card h-100">
              <div class="card-body p-3">
                <h6 class="font-weight-bold mb-3">Event Types</h6>
                <div v-if="data.eventTypeBreakdown && data.eventTypeBreakdown.length">
                  <div
                    v-for="row in data.eventTypeBreakdown"
                    :key="row.event_type"
                    class="mb-2"
                  >
                    <div class="d-flex justify-content-between align-items-center mb-1">
                      <span
                        class="text-capitalize search-link"
                        @click="drillSearch(row.event_type, 'events')"
                        style="cursor:pointer;"
                        title="Search this event type"
                      >{{ row.event_type || 'unknown' }}</span>
                      <span class="text-muted">{{ fmt(row.count) }}</span>
                    </div>
                    <div class="progress" style="height:6px;border-radius:3px;">
                      <div
                        class="progress-bar"
                        :class="typeBarClass(row.event_type)"
                        :style="{ width: barWidth(row.count, data.eventTypeBreakdown[0].count) + '%' }"
                      ></div>
                    </div>
                  </div>
                </div>
                <p v-else class="text-muted mb-0">No data yet.</p>
              </div>
            </div>
          </div>

          <!-- Top feature events -->
          <div class="col-md-7 mb-4">
            <div class="card flat-card h-100">
              <div class="card-body p-3">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <h6 class="font-weight-bold mb-0">Top Feature Events</h6>
                  <small class="text-muted">frontend only</small>
                </div>
                <div v-if="features && features.length" style="max-height:260px;overflow-y:auto;">
                  <table class="table mb-0">
                    <thead class="thead-light">
                      <tr>
                        <th>Event</th>
                        <th>Type</th>
                        <th class="text-right">Count</th>
                        <th class="text-right">Companies</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="row in features"
                        :key="row.event_name"
                        class="search-link"
                        style="cursor:pointer;"
                        @click="drillSearch(row.event_name, 'events')"
                        title="Search this event"
                      >
                        <td><code >{{ row.event_name }}</code></td>
                        <td><span class="badge badge-light text-capitalize">{{ row.event_type }}</span></td>
                        <td class="text-right">{{ fmt(row.count) }}</td>
                        <td class="text-right">{{ row.companies_using }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p v-else class="text-muted text-center py-3 mb-0">No frontend events tracked yet.</p>
              </div>
            </div>
          </div>

          <!-- Most active companies -->
          <div class="col-md-6 mb-4">
            <div class="card flat-card">
              <div class="card-body p-3">
                <h6 class="font-weight-bold mb-3">Most Active Companies</h6>
                <div v-if="data.mostActiveCompanies && data.mostActiveCompanies.length" style="max-height:280px;overflow-y:auto;">
                  <table class="table mb-0">
                    <thead class="thead-light">
                      <tr>
                        <th>#</th>
                        <th>Company</th>
                        <th class="text-right">Events</th>
                        <th class="text-right">Users</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, i) in data.mostActiveCompanies" :key="row.company_id">
                        <td class="text-muted">{{ i + 1 }}</td>
                        <td class="font-weight-bold">{{ row.company_name }}</td>
                        <td class="text-right">{{ fmt(row.count) }}</td>
                        <td class="text-right">{{ row.unique_users }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p v-else class="text-muted text-center py-3 mb-0">No data yet.</p>
              </div>
            </div>
          </div>

          <!-- Most active users -->
          <div class="col-md-6 mb-4">
            <div class="card flat-card">
              <div class="card-body p-3">
                <h6 class="font-weight-bold mb-3">Most Active Staff</h6>
                <div v-if="data.mostActiveUsers && data.mostActiveUsers.length" style="max-height:280px;overflow-y:auto;">
                  <table class="table mb-0">
                    <thead class="thead-light">
                      <tr>
                        <th>#</th>
                        <th>User</th>
                        <th class="text-right">Events</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, i) in data.mostActiveUsers" :key="row.user_id">
                        <td class="text-muted">{{ i + 1 }}</td>
                        <td>
                          <div class="font-weight-bold">{{ row.name }}</div>
                          <div class="text-muted" style="font-size:.7rem;">{{ row.email }}</div>
                        </td>
                        <td class="text-right">{{ fmt(row.count) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p v-else class="text-muted text-center py-3 mb-0">No data yet.</p>
              </div>
            </div>
          </div>

          <!-- Most hit routes -->
          <div class="col-12 mb-4">
            <div class="card flat-card">
              <div class="card-body p-3">
                <h6 class="font-weight-bold mb-3">Most Hit Routes</h6>
                <div v-if="data.topPages && data.topPages.length" style="max-height:260px;overflow-y:auto;">
                  <table class="table mb-0">
                    <thead class="thead-light">
                      <tr>
                        <th>Route</th>
                        <th class="text-right" style="width:100px;">Hits</th>
                        <th style="width:200px;"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="row in data.topPages"
                        :key="row.route"
                        class="search-link"
                        style="cursor:pointer;"
                        @click="drillSearch(trimRoute(row.route), 'routes')"
                        title="Search this route"
                      >
                        <td><code class="text-break">{{ trimRoute(row.route) }}</code></td>
                        <td class="text-right">{{ fmt(row.count) }}</td>
                        <td>
                          <div class="progress" style="height:6px;border-radius:3px;">
                            <div
                              class="progress-bar bg-dark"
                              :style="{ width: barWidth(row.count, data.topPages[0].count) + '%' }"
                            ></div>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p v-else class="text-muted text-center py-3 mb-0">No data yet.</p>
              </div>
            </div>
          </div>
        </div>
      </template>

      <div v-else-if="error" class="alert alert-danger">
        Failed to load analytics: {{ error }}
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'AdminEventAnalytics',

  data() {
    return {
      days:    30,
      loading: false,
      error:   null,

      data:     null,
      timeline: [],
      features: [],

      featuresBreakdown:        [],
      featuresBreakdownLoading: false,

      search: {
        query:   '',
        type:    'all',
        loading: false,
        error:   null,
        results: null,
        timer:   null,
      },

      searchTypes: [
        { value: 'all',     label: 'All'     },
        { value: 'routes',  label: 'Routes'  },
        { value: 'events',  label: 'Events'  },
        { value: 'feature', label: 'Feature' },
      ],
    };
  },

  computed: {
    maxTimelineTotal() {
      return Math.max(...this.timeline.map(r => r.total), 1);
    },
  },

  mounted() {
    this.load();
  },

  methods: {
    // ── Dashboard load ───────────────────────────────────────────────────────
    async load() {
      this.loading = true;
      this.error   = null;
      this.featuresBreakdownLoading = true;

      try {
        const [eventsRes, timelineRes, featuresRes, breakdownRes] = await Promise.all([
          axios.get(`/api/admin/analytics/events?days=${this.days}`),
          axios.get(`/api/admin/analytics/timeline?days=${this.days}`),
          axios.get(`/api/admin/analytics/features?days=${this.days}`),
          axios.get(`/api/admin/analytics/feature-breakdown?days=${this.days}`),
        ]);

        this.data              = eventsRes.data;
        this.features          = featuresRes.data;
        this.timeline          = this.flattenTimeline(timelineRes.data);
        this.featuresBreakdown = breakdownRes.data;
      } catch (e) {
        this.error = e?.response?.data?.message || e.message;
      } finally {
        this.loading                  = false;
        this.featuresBreakdownLoading = false;
      }
    },

    // ── Search ───────────────────────────────────────────────────────────────
    onSearchInput() {
      clearTimeout(this.search.timer);
      if (this.search.query.length < 2) {
        this.search.results = null;
        return;
      }
      this.search.timer = setTimeout(() => this.runSearch(), 350);
    },

    async runSearch() {
      if (this.search.query.length < 2) return;

      this.search.loading = true;
      this.search.error   = null;
      this.search.results = null;

      try {
        const res = await axios.get('/api/admin/analytics/search', {
          params: {
            q:    this.search.query,
            type: this.search.type,
            days: this.days,
          },
        });
        this.search.results = res.data;
      } catch (e) {
        this.search.error = e?.response?.data?.message || e.message;
      } finally {
        this.search.loading = false;
      }
    },

    clearSearch() {
      this.search.query   = '';
      this.search.results = null;
      this.search.error   = null;
    },

    // Click a row in the dashboard to prefill the search box and run a search.
    // type can be 'routes' | 'events' | 'feature' (feature name drill-down).
    drillSearch(term, type) {
      this.search.query = term;
      this.search.type  = type || 'all';
      this.runSearch();
      this.$nextTick(() => {
        const el = this.$el.querySelector('input[type=text]');
        if (el) { el.focus(); el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
      });
    },

    // ── Helpers ──────────────────────────────────────────────────────────────
    flattenTimeline(raw) {
      return Object.entries(raw).map(([date, sources]) => ({
        date,
        frontend:   sources.frontend?.count   || 0,
        middleware: sources.middleware?.count  || 0,
        backend:    sources.backend?.count     || 0,
        total: (sources.frontend?.count || 0) + (sources.middleware?.count || 0) + (sources.backend?.count || 0),
      }));
    },

    fmt(n) {
      if (n == null) return '—';
      return Number(n).toLocaleString();
    },

    barWidth(value, max) {
      if (!max) return 0;
      return Math.max(2, Math.round((value / max) * 100));
    },

    trimRoute(route) {
      try {
        const url = new URL(route);
        return url.pathname + (url.search || '');
      } catch {
        return route || '—';
      }
    },

    timeAgo(dateStr) {
      if (!dateStr) return '—';
      const diff = Date.now() - new Date(dateStr).getTime();
      const mins  = Math.floor(diff / 60000);
      if (mins < 60)  return `${mins}m ago`;
      const hrs   = Math.floor(mins / 60);
      if (hrs < 24)   return `${hrs}h ago`;
      const days  = Math.floor(hrs / 24);
      return `${days}d ago`;
    },

    // Wrap matching text in a highlight span — only plain text, never HTML injection
    highlight(text, query) {
      if (!text || !query) return this.esc(text);
      const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return this.esc(text).replace(
        new RegExp(escaped.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'),
        m => `<mark class="px-0 rounded" style="background:#fff3cd;">${m}</mark>`
      );
    },

    esc(str) {
      if (!str) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    },

    typeBarClass(type) {
      const map = {
        pos:         'bg-primary',
        checkout:    'bg-dark',
        inventory:   'bg-dark',
        navigation:  'bg-primary',
        customer:    'bg-secondary',
        api_request: 'bg-light border',
        report:      'bg-danger',
      };
      return map[type] || 'bg-secondary';
    },
  },
};
</script>

<style scoped>
.search-link:hover td {
  background: #f8fafc;
}
.search-link:hover code {
  color: #0056b3;
}
.feature-card {
  transition: box-shadow .15s, transform .15s;
  border: 1px solid #e9ecef !important;
}
.feature-card:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,.06) !important;
  transform: translateY(-1px);
}
.flat-card {
  border: 1px solid #e9ecef;
  border-radius: 14px;
}
.section-title {
  color: #0f172a;
  font-weight: 600;
}
.pill-input {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 6px 12px;
  background: #fff;
  font-size: 13px;
  color: #0f172a;
}
.pill-input input {
  border: none;
  outline: none;
  background: transparent;
  font-size: 13px;
  color: #0f172a;
  flex-grow: 1;
  min-width: 0;
}
</style>
